import { asDate } from "@@/shared/date-time/as-date";
import type { DateRangeId } from "@@/shared/date-time/date-range-id";
import {
    dateRangeIdFactory,
    dateRangeIdPrefix,
    dateRangeIdZodSchema,
} from "@@/shared/date-time/date-range-id";
import { isTimeRange, type TimeRange } from "@@/shared/date-time/time-range";
import {
    durationInDaysZodSchema,
    type DurationInDays,
} from "@@/shared/date-time/time-spans";
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    isBefore,
    isSameDay,
    nextFriday,
    nextMonday,
    nextSaturday,
    nextSunday,
    parseISO,
    startOfToday,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import objectHash from "object-hash";
import { z } from "zod";
import { defaultTimeZone } from "../weekly-schedules/default-time-zone";
import {
    isoAndUnixFactory,
    isoAndUnixZodSchema,
    type IsoAndUnixTimestamp,
} from "./iso-and-unix";
import { endOfDayZoned, startOfDayZoned } from "./start-and-end-of-day-zoned";
import type { Timezone } from "./timezone";
import { timezoneZodType } from "./timezone";

const dateRangeZodObject = z.object({
    _id: dateRangeIdZodSchema,
    _type: z.literal("DATE_RANGE"),
    /** Start of day in given or default time zone (not sure if all old is correctly set to start of day or if they may contain just an arbitrary date time) */
    start: isoAndUnixZodSchema,
    /** Start of day in given or default time zone (not sure if all old is correctly set to start of day or if they may contain just an arbitrary date time) */
    end: isoAndUnixZodSchema,
    numberOfDays: durationInDaysZodSchema,
    // TimeZone is required for all date ranges but hasn't always been required.
    // TimeZone will be defaulted to the defaultTimeZone since all were that
    // were created without the timezone property
    timeZone: timezoneZodType.default(defaultTimeZone).optional(),
});

type DateRange = z.infer<typeof dateRangeZodObject>;
const dateRangeSingleDateZodObject = dateRangeZodObject.refine(value => {
    return value.start.iso === value.end.iso;
});
type DateRange_SingleDate = z.infer<typeof dateRangeSingleDateZodObject>;

const dateRangeFactory = {
    from: (params: {
        start: IsoAndUnixTimestamp | Date;
        end?: IsoAndUnixTimestamp | Date;
        timezone?: Timezone;
        id?: DateRangeId;
    }): DateRange => {
        const { start, end } = params;
        const _start = asDate(start);
        const _end = asDate(end ?? _start);
        const _timezone = params.timezone ?? defaultTimeZone;

        // TODO! DIFFERENCE IN DAYS IS NOT CALCULATED IN THE SPECIFIED TIMEZONE
        const durationInDays = (Math.abs(differenceInDays(_end, _start)) +
            1) as DurationInDays;

        const startZoned = startOfDayZoned(_start, _timezone);
        const endZoned = startOfDayZoned(_end, _timezone);
        const endIsBeforeStart = isBefore(endZoned, startZoned);

        return {
            _id: params.id ?? dateRangeIdFactory(),
            _type: "DATE_RANGE",
            start: endIsBeforeStart
                ? isoAndUnixFactory(endZoned)
                : isoAndUnixFactory(startZoned),
            end: endIsBeforeStart
                ? isoAndUnixFactory(startZoned)
                : isoAndUnixFactory(endZoned),
            numberOfDays: durationInDays,
            timeZone: _timezone,
        } satisfies DateRange;
    },
    fromTimeRange: (params: {
        timeRange: TimeRange;
        timezone?: Timezone;
    }): DateRange => {
        const { timeRange } = params;
        return dateRangeFactory.from({
            start: timeRange.start,
            end: timeRange.end,
            timezone: params.timezone,
        });
    },
    fromDateRange: (params: {
        dateRange: DateRange;
        timezone?: Timezone;
    }): DateRange => {
        const { dateRange } = params;
        if (!isValidDateRange(dateRange))
            throw new Error(
                `Date range was not valid, start and end dates were not start of day in the correct time zone. Start: ${dateRange.start.iso}, End: ${dateRange.end.iso}, TimeZone: ${typeof dateRange?.timeZone === "string" ? dateRange.timeZone : defaultTimeZone}`,
            );

        return dateRangeFactory.from({
            start: dateRange.start,
            end: dateRange.end,
            timezone: params.timezone,
        });
    },
    fromTimeOrDateRange: (params: {
        timeOrDateRange: DateRange | TimeRange;
        timezone?: Timezone;
    }): DateRange => {
        if (isTimeRange(params.timeOrDateRange)) {
            return dateRangeFactory.fromTimeRange({
                timeRange: params.timeOrDateRange,
                timezone: params.timezone,
            });
        }
        return dateRangeFactory.fromDateRange({
            dateRange: params.timeOrDateRange,
            timezone: params.timezone,
        });
    },
    fromStartAndNumberOfDays: (params: {
        start: Date;
        numberOfDays: DurationInDays;
        timezone?: Timezone;
    }): DateRange => {
        const { start, numberOfDays } = params;
        return dateRangeFactory.from({
            start,
            end: addDays(start, numberOfDays - 1),
            timezone: params.timezone,
        });
    },
    fromStartOrTimeRange: (params: {
        start: IsoAndUnixTimestamp | Date | TimeRange;
        timezone?: Timezone;
    }): DateRange => {
        const { start } = params;
        if (isTimeRange(start)) {
            return dateRangeFactory.fromTimeRange({
                timeRange: start,
                timezone: params.timezone,
            });
        }
        return dateRangeFactory.from({ start, timezone: params.timezone });
    },
    fromDateRangeString: (params: {
        dateRangeString: DateRangeString;
        /**
         * timeZone, if given and different from original timeZone
         * of date range string it converts the date range to the given timezone
         */
        timezone?: Timezone;
    }): DateRange => {
        const { dateRangeString } = params;
        const _dateRange = dateRangeFromString(dateRangeString);
        // console.log(
        //     "test dateRange",
        //     _dateRange,
        //     dateRangeFactory.fromDateRange({
        //         dateRange: _dateRange,
        //         timezone: params.timezone,
        //     }),
        // );
        if (!params.timezone) return _dateRange;
        return dateRangeFactory.fromDateRange({
            dateRange: _dateRange,
            timezone: params.timezone,
        });
    },
    /**
     * Date range start will be `start of day of the first day of the year` in given or default timeZone \
     * Date range end will be `start of day of the last day of the year` in given or default timeZone
     */
    forYears: (params: {
        fromYear: number;
        toYear?: number;
        timezone?: Timezone;
    }) => {
        const timezone = params.timezone ?? defaultTimeZone;
        // Calculate the start of the first day of the year in given timezone
        const startOfYearZoned = startOfDayZoned(
            new Date(params.fromYear, 0, 1),
            timezone,
        );
        // Calculate the end of the last day of the year in given timezone
        const endOfYearZoned = startOfDayZoned(
            new Date(params.toYear ?? params.fromYear, 11, 31),
            timezone,
        );
        return dateRangeFactory.from({
            start: startOfYearZoned,
            end: endOfYearZoned,
            timezone: timezone,
        });
    },
};

/**
 * Represents a date range string in a specific format.
 *
 * The format can be one of the following:
 * - `DR|startDate|endDate`
 * - `DR|startDate|endDate|timeZone`
 *
 * Where `startDate`, `endDate`, and `timeZone` are strings. The dates are in ISO8601 format. And the `timeZone` is a string representing a time zone, e.g. `Europe/Stockholm`.
 * TimeZone will default to `Europe/Stockholm` if not provided.
 */
type DateRangeString =
    | `DR|${string}|${string}`
    | `DR|${string}|${string}|${string}`;

/**
 * Converts a DateRange object to a string representation.
 *
 * @param dateRange - The DateRange object containing start and end dates.
 * @param timezone - The time zone to be included in the string representation. Defaults to `defaultTimeZone` which is `Europe/Stockholm`.
 * @returns A string representation of the DateRange in the format `DR|startISO|endISO|timeZone`.
 */
function dateRangeToString(
    dateRange: undefined,
    timezone?: Timezone,
): undefined;
function dateRangeToString(
    dateRange: DateRange,
    timezone?: Timezone,
): DateRangeString;
function dateRangeToString(
    dateRange: DateRange | undefined,
    timezone?: Timezone,
): DateRangeString | undefined;
function dateRangeToString(
    dateRange: DateRange | undefined,
    timezone: Timezone = defaultTimeZone,
): DateRangeString | undefined {
    if (!dateRange) return undefined;
    return `DR|${dateRange.start.iso}|${dateRange.end.iso}|${timezone}`;
}
const isDateRangeString = (item: unknown): item is DateRangeString => {
    if (typeof item !== "string") return false;
    return item.startsWith("DR|");
};
const dateRangeStringZodType = z.string().refine(value => {
    return isDateRangeString(value);
}) as z.ZodType<DateRangeString>;
const dateRangeFromString = (item: DateRangeString): DateRange => {
    const [startIso, endIso, timezone] = item.split("|").slice(1);
    const start = parseISO(startIso);
    const end = parseISO(endIso);
    const _timezoneOrFallback = (timezone ?? defaultTimeZone) as Timezone;
    const _timezone = timezone as Timezone | undefined;

    // Make sure the start and end date is
    // start of day in the correct time zone
    const startZoned = startOfDayZoned(start, _timezoneOrFallback);
    const endZoned = startOfDayZoned(end, _timezoneOrFallback);
    if (
        isoAndUnixFactory(startZoned).iso !== isoAndUnixFactory(start).iso ||
        isoAndUnixFactory(endZoned).iso !== isoAndUnixFactory(end).iso
    ) {
        // If they are not, we really don't know what date range is supposed to be
        // and we should not create a date range from it, and we should throw
        // an error and try to correct it manually
        // eslint-disable-next-line no-console
        console.error(
            `Invalid date range string "${item}". Start and end dates are not start of day in the time zone of the date range. Automatically correcting it to start of day in time zone: "${_timezoneOrFallback}".`,
        );

        // throw new Error(
        //     `Invalid date range string "${item}". Start and end dates are not start of day in the time zone of the date range.`,
        // );
    }

    /**
     * Make sure the same start, end, timezone creates
     * the same id every time
     */
    const dateRangeId = ((): DateRangeId => {
        const hash = objectHash({
            start: startIso,
            end: endIso,
            timezone: _timezone,
        });
        return `${dateRangeIdPrefix}${hash}` as DateRangeId;
    })();

    return dateRangeFactory.from({
        id: dateRangeId,
        start: startZoned,
        end: endZoned,
        timezone: _timezone,
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDateRange = (item: any): item is DateRange => {
    return typeof item !== "undefined" && item?._type === "DATE_RANGE";
};
/**
 * If start and end iso strings are equal, the date ranges are considered equal. \
 * Compares two DateRange objects to determine if they are equal.
 *
 * @param dateRange1 - The first DateRange object to compare.
 * @param dateRange2 - The second DateRange object to compare.
 * @returns A boolean indicating whether the two DateRange objects are equal. Have the same start and end dates that is.
 */
const isEqualDateRange = (
    dateRange1: DateRange,
    dateRange2: DateRange,
): boolean => {
    return (
        dateRange1.start.iso === dateRange2.start.iso &&
        dateRange1.end.iso === dateRange2.end.iso
    );
};

const largerThen = (input1: number, input2: number, orEqual = true) => {
    return orEqual ? input1 >= input2 : input1 > input2;
};

/**
 * Checks if a given date or date range is within a specified date range.
 * Within means that the input in it's whole is equal or larger then the start date and smaller or equal to the end date.
 *
 * @param input - The date or date range to check. Can be a `DateRange`, `IsoAndUnixTimestamp`, or `Date`.
 * @param dateRange - The date range to compare against.
 * @param options - Optional settings for the comparison.
 * @param options.strict - When true, compares using `>=` and `<=` instead of `>` and `<`. Defaults to `true`.
 * @returns `true` if the input is within the date range, otherwise `false`.
 */
function isWithinDateRange(
    input: DateRange | IsoAndUnixTimestamp | Date,
    dateRange: DateRange,
): boolean {
    const timeZone = dateRange.timeZone ?? defaultTimeZone;
    const start = (() => {
        if (isDateRange(input)) return input.start;
        if (input instanceof Date)
            return isoAndUnixFactory(startOfDayZoned(input, timeZone));
        return isoAndUnixFactory(startOfDayZoned(asDate(input), timeZone));
    })();
    const end = (() => {
        if (isDateRange(input)) return input.end;
        // If input is a date or isoAndUnixTimestamp
        // we've already handled the start of day conversion
        // and end is the same as start
        return start;
    })();

    if (
        largerThen(start.unix, dateRange.start.unix, true) &&
        largerThen(dateRange.end.unix, end.unix, true)
    ) {
        return true;
    }

    return false;
}

/**
 * Checks if a given date or date range is part of another date range.
 * Part of means that the input is either fully or partially within the specified date range.
 *
 * @param input - The date or date range to check. Can be of type `DateRange` or `IsoAndUnixTimestamp`.
 * @param dateRange - The date range within which to check.
 * @param options - Optional settings.
 * @param options.strict - If `true`, performs a strict comparison. Defaults to `true`.
 *
 * @returns `true` if the input date or date range is part of the specified date range, otherwise `false`.
 */
function isPartOfDateRange(
    input: DateRange | IsoAndUnixTimestamp | Date,
    dateRange: DateRange,
): boolean {
    const timeZone = dateRange.timeZone ?? defaultTimeZone;
    const start = (() => {
        if (isDateRange(input)) return input.start;
        if (input instanceof Date)
            return isoAndUnixFactory(startOfDayZoned(input, timeZone));
        return isoAndUnixFactory(startOfDayZoned(asDate(input), timeZone));
    })();
    const end = (() => {
        if (isDateRange(input)) return input.end;
        // If input is a date or isoAndUnixTimestamp
        // we've already handled the start of day conversion
        // and end is the same as start
        return start;
    })();

    // Case 1: Input's start date is within the date range
    if (
        largerThen(start.unix, dateRange.start.unix, true) &&
        largerThen(dateRange.end.unix, start.unix, true)
    ) {
        return true;
    }

    // Case 2: Input's end date is within the date range
    if (
        largerThen(end.unix, dateRange.start.unix, true) &&
        largerThen(dateRange.end.unix, end.unix, true)
    ) {
        return true;
    }

    // Case 3: Input completely contains the date range
    if (
        largerThen(dateRange.start.unix, start.unix, true) &&
        largerThen(end.unix, dateRange.end.unix, true)
    ) {
        return true;
    }

    return false;
}

/**
 * Generates an array of dates within a specified date range, formatted as ISO8601 and Unix timestamps.
 *
 * @param dateRange - An object containing the start and end dates of the range, both in ISO8601 format.
 * @param timeZone - (Optional) A string representing the time zone to use. If not provided, the time zone from the dateRange object or a default time zone will be used.
 * @returns An array of objects, each containing the date formatted as ISO8601 and Unix timestamps.
 */
const getDatesInDateRange = (
    dateRange: DateRange,
    timeZone?: Timezone,
): IsoAndUnixTimestamp[] => {
    // Parse the ISO8601 strings into Date objects
    const _timeZone = timeZone ?? dateRange.timeZone ?? defaultTimeZone;
    const startDateZoned = toZonedTime(
        parseISO(dateRange.start.iso),
        _timeZone,
    );
    const endDateZoned = toZonedTime(parseISO(dateRange.end.iso), _timeZone);

    const dates: IsoAndUnixTimestamp[] = [];
    let currentDateZoned = startDateZoned;
    // Loop until current date is after the end date
    while (
        isBefore(currentDateZoned, endDateZoned) ||
        currentDateZoned.getTime() === endDateZoned.getTime()
    ) {
        // Format the date as ISO8601 and add it to the list
        dates.push(isoAndUnixFactory(currentDateZoned));

        // Move to the next day
        currentDateZoned = addDays(currentDateZoned, 1);
    }
    return dates;
};

/**
 * Converts the start and end dates of a given date range to the specified time zone.
 *
 * @param dateRange - The date range containing the start and end dates to be converted.
 * @param timeZone - (Optional) The time zone to which the dates should be converted.
 *                   If not provided, the function will use the time zone specified in the date range
 *                   or a default time zone.
 * @returns An object containing the start and end dates converted to the specified or default time zone.
 */
const getZonedDateRangeDates = (dateRange: DateRange, timeZone?: Timezone) => {
    const _timeZone = timeZone ?? dateRange.timeZone ?? defaultTimeZone;
    return {
        start: toZonedTime(asDate(dateRange.start), _timeZone),
        end: toZonedTime(asDate(dateRange.end), _timeZone),
    };
};

/**
 * Checks if two date ranges are the same and considers specified time zone.
 * If start and end of the date ranges are the same day, the date ranges are considered the same. \
 * If time zone is not provided, the time zone from the date ranges (if any) or a default time zone will be used.
 * If the time zone is different, the dates will be converted to the specified time zone before comparison.
 * If the time zone is the same, the comparison will be made directly.
 *
 * @param dateRange1 - The first date range to compare.
 * @param dateRange2 - The second date range to compare.
 * @param timezone - Optional. The time zone to consider for the comparison. If not provided, the time zone from the date ranges or a default time zone will be used.
 * @returns `true` if both date ranges are the same when considering the specified time zone, otherwise `false`.
 */
const isSameDateRangeZoned = (
    dateRange1: DateRange,
    dateRange2: DateRange,
    timezone?: Timezone,
): boolean => {
    const timezone1 = timezone ?? dateRange1.timeZone ?? defaultTimeZone;
    const dateRange1Zoned = getZonedDateRangeDates(dateRange1, timezone1);
    const timezone2 = timezone ?? dateRange2.timeZone ?? defaultTimeZone;
    const dateRange2Zoned = getZonedDateRangeDates(dateRange2, timezone2);
    return (
        isSameDay(dateRange1Zoned.start, dateRange2Zoned.start) &&
        isSameDay(dateRange1Zoned.end, dateRange2Zoned.end)
    );
};

/**
 * Checks if a given date range represents only one date.
 *
 * @param dateRange - The date range to check. If undefined, the function returns false.
 * @param timeZone - Optional time zone to use for the comparison. If not provided, the function uses the time zone from the date range or a default time zone.
 * @returns `true` if the start and end of the date range are the same day, otherwise `false`.
 */
const isDateRangeOnlyOneDate = (
    dateRange: DateRange | undefined,
    timeZone?: Timezone,
): boolean => {
    if (!dateRange) return false;
    const start = startOfDayZoned(
        asDate(dateRange.start),
        timeZone ?? dateRange.timeZone ?? defaultTimeZone,
    );
    const end = startOfDayZoned(
        asDate(dateRange.end),
        timeZone ?? dateRange.timeZone ?? defaultTimeZone,
    );
    return start.toISOString() === end.toISOString();
};

/**
 * Checks if start and end dates in date range are start of day in the given time zone
 * if not, logs a warning and returns false
 */
const isValidDateRange = (dateRange: DateRange): boolean => {
    const startIso = isoAndUnixFactory(
        startOfDayZoned(
            asDate(dateRange.start),
            dateRange.timeZone ?? defaultTimeZone,
        ),
    ).iso;
    const endIso = isoAndUnixFactory(
        startOfDayZoned(
            asDate(dateRange.end),
            dateRange.timeZone ?? defaultTimeZone,
        ),
    ).iso;
    if (startIso !== dateRange.start.iso) {
        // eslint-disable-next-line no-console
        console.error(
            `Start date in date range is not start of day in correct time zone. Expected: ${startIso}, Actual: ${dateRange.start.iso}`,
        );
    }
    if (endIso !== dateRange.end.iso) {
        // eslint-disable-next-line no-console
        console.error(
            `End date in date range is not start of day in correct time zone. Expected: ${startIso}, Actual: ${dateRange.start.iso}`,
        );
    }
    return startIso === dateRange.start.iso && endIso === dateRange.end.iso;
};

/**
 * Gets the end of the day in the specified time zone for the end date of a given date range.
 *
 * @param dateRange - The date range object containing the start and end dates, and optionally a time zone.
 * @returns The end of the day in the specified time zone for the end date of the date range.
 */
const getEndOfDayOfDateRangeEnd = (dateRange: DateRange): Date => {
    return endOfDayZoned(
        asDate(dateRange.end),
        dateRange.timeZone ?? defaultTimeZone,
    );
};

/**
 * Creates a date range starting today.
 * The range will only contain the today's date.
 *
 * @returns {DateRange} The date range starting today.
 */
const today = (): DateRange => {
    return dateRangeFactory.from({
        start: new Date(),
    });
};

/**
 * Creates a date range starting from tomorrow.
 * The range will only contain the date for tomorrow.
 *
 * @returns {DateRange} The date range starting from tomorrow.
 */
const tomorrow = (): DateRange => {
    return dateRangeFactory.from({
        start: addDays(new Date(), 1),
    });
};

/**
 * Creates a date range starting from the next Friday.
 * The range will only contain the date for the next Friday.
 *
 * @returns {DateRange} The date range starting from the next Friday.
 */
const onFriday = (): DateRange => {
    // create a date range for the weekend
    const now = new Date();
    const friday = nextFriday(now);
    return dateRangeFactory.from({
        start: friday,
    });
};

/**
 * Creates a date range starting from the next Saturday.
 * The range will only contain the date for the next Saturday.
 *
 * @returns {DateRange} The date range starting from the next Saturday.
 */
const onSaturday = (): DateRange => {
    // create a date range for the weekend
    const now = new Date();
    const saturday = nextSaturday(now);
    return dateRangeFactory.from({
        start: saturday,
    });
};

/**
 * Creates a date range starting from the next Sunday.
 * The range will only contain the date for the next Sunday.
 *
 * @returns {DateRange} The date range starting from the next Sunday.
 */
const onSunday = (): DateRange => {
    // create a date range for the weekend
    const now = new Date();
    const sunday = nextSunday(now);
    return dateRangeFactory.from({
        start: sunday,
    });
};

/**
 * Returns a DateRange object representing the day after tomorrow.
 * The range will only contain the date for the day after tomorrow.
 *
 * @returns {DateRange} A DateRange object with the start date set to two days from the current date.
 */
const dayAfterTomorrow = (): DateRange => {
    return dateRangeFactory.from({
        start: addDays(new Date(), 2),
    });
};

/**
 * Generates a date range for the current week.
 * The week is considered to start from Monday and end on Sunday.
 * If today is Saturday or Sunday, the week will start from the following Monday.
 *
 * @param {boolean} [beginToday=true] - If true, the week starts from today; otherwise, it starts from the previous Monday.
 * @returns {DateRange} - An object representing the start and end dates of the week.
 */
const thisWeek = (beginToday: boolean = true): DateRange => {
    // create a date range for the week
    const now = new Date();
    const weekStart = beginToday ? now : nextMonday(addWeeks(now, -1));
    const weekEnd = nextSunday(now);
    return dateRangeFactory.from({
        start: weekStart,
        end: weekEnd,
    });
};
/**
 * Generates a date range from today (or a given `startAt` date) and one month into the future.
 */
const oneWeek = (startAt: Date = startOfToday()): DateRange => {
    // create a date range for the week
    const endAt = addWeeks(startAt, 1);
    return dateRangeFactory.from({
        start: startAt,
        end: endAt,
    });
};
/**
 * Generates a date range from today (or a given `startAt` date) and one month into the future.
 */
const oneMonth = (startAt: Date = startOfToday()): DateRange => {
    // create a date range for the week
    const endAt = addMonths(startAt, 1);
    return dateRangeFactory.from({
        start: startAt,
        end: endAt,
    });
};
/**
 * Generates a date range for the upcoming week.
 * The date range will start from next Monday.
 * The end date will be the following Sunday.
 * @returns {DateRange} An object representing the start and end dates of the next week.
 */
const nextWeek = (): DateRange => {
    // create a date range for the week
    const now = new Date();
    const weekStart = nextMonday(now);
    const weekEnd = nextSunday(weekStart);
    return dateRangeFactory.from({
        start: weekStart,
        end: weekEnd,
    });
};
/**
 * Calculates the date range for the next weekend.
 * The weekend is considered to be Saturday and Sunday.
 * If today is Saturday or Sunday, the date range will start next Saturday.
 *
 * @returns {DateRange} An object representing the date range for the next weekend.
 */
const nextWeekend = (): DateRange => {
    const now = new Date();
    const weekStart = nextSaturday(now);
    return dateRangeFactory.fromStartAndNumberOfDays({
        start: weekStart,
        numberOfDays: 2 as DurationInDays,
    });
};

/**
 * Creates a DateRange object representing the range starting from yesterday.
 * The range will only contain the yesterday's date.
 *
 * @returns {DateRange} A DateRange object with the start date set to yesterday.
 */
const yesterday = (): DateRange => {
    return dateRangeFactory.from({
        start: addDays(new Date(), -1),
    });
};

/**
 * Transform an array of date ranges to a single date range \
 * NOTE! needs to contain at least one date range
 */
const mergeDateRanges = (dateRanges: DateRange[]): DateRange => {
    if (!dateRanges.length) {
        // There must be at least one
        throw new Error("Cannot merge empty array of date ranges");
    }
    if (dateRanges.length === 1) {
        // "There can be only one"
        return dateRanges[0];
    }
    const start = dateRanges.reduce((previous, current) => {
        return isBefore(asDate(current.start), asDate(previous.start))
            ? current
            : previous;
    }).start;
    const end = dateRanges.reduce((previous, current) => {
        return isBefore(asDate(previous.end), asDate(current.end))
            ? current
            : previous;
    }).end;
    return dateRangeFactory.from({ start, end });
};

/**
 * Transform an array of date ranges and time ranges into a date range
 * NOTE! needs to contain at least one date or time range
 */
const mergeDateAndTimeRanges = (
    ranges: Array<DateRange | TimeRange>,
    timezone: Timezone = defaultTimeZone,
): DateRange => {
    const dateRanges = ranges.filter(range => isDateRange(range));
    const timeRanges = ranges.filter(range => isTimeRange(range));
    if (!dateRanges.length && !timeRanges.length) {
        // There must be at least one
        throw new Error("Cannot merge empty array of date and time ranges");
    }
    return mergeDateRanges([
        ...dateRanges,
        ...timeRanges.map(timeRange =>
            dateRangeFactory.fromTimeRange({ timeRange, timezone }),
        ),
    ]);
};

/**
 * Utility function that splits a date range into an array of DateRangeSingleDate objects.
 */
const splitDateRangeIntoSingleDateDateRanges = (
    dateRange: DateRange,
): DateRange_SingleDate[] => {
    const dates = getDatesInDateRange(dateRange);
    return dates.map(date => {
        return dateRangeFactory.from({
            start: date,
            end: date,
            timezone: dateRange.timeZone,
        }) as unknown as DateRange_SingleDate;
    });
};

const dateRangeUtils = {
    dateRangeFactory,
    today,
    tomorrow,
    yesterday,
    dayAfterTomorrow,
    onFriday,
    onSaturday,
    onSunday,
    thisWeek,
    nextWeek,
    nextWeekend,
    oneWeek,
    oneMonth,
    fromString: dateRangeFromString,
    toString: dateRangeToString,
    getDates: getDatesInDateRange,
    getEndOfDayOfDateRangeEnd,
    isDateRange,
    isOnlyOneDate: isDateRangeOnlyOneDate,
    isDateRangeString,
    isEqual: isEqualDateRange,
    isPartOf: isPartOfDateRange,
    isSameDay: isSameDateRangeZoned,
    isValid: isValidDateRange,
    isWithin: isWithinDateRange,
    mergeDateRanges,
    mergeDateAndTimeRanges,
    split: splitDateRangeIntoSingleDateDateRanges,
};

export {
    dateRangeFactory,
    dateRangeFromString,
    dateRangeSingleDateZodObject,
    dateRangeStringZodType,
    dateRangeToString,
    dateRangeUtils,
    dateRangeZodObject,
    getDatesInDateRange,
    getEndOfDayOfDateRangeEnd,
    isDateRange,
    isDateRangeOnlyOneDate,
    isDateRangeString,
    isEqualDateRange,
    isPartOfDateRange,
    isSameDateRangeZoned,
    isValidDateRange,
    isWithinDateRange,
};
export type { DateRange, DateRange_SingleDate, DateRangeString };
